/**
 * Demo overlay branding (FLO-64).
 *
 * The demo overlay (DemoView) renders to plain DOM — NOT a shadow root — and
 * styles itself with module-level color constants. To let a customer's brand
 * accent flow through without threading a prop into every styled element, we
 * expose the accent palette as CSS custom properties on the DemoView root and
 * have those constants read `var(--floe-accent*, <default>)`. When no accent
 * is configured we set no variables, so the hand-tuned Floe coral defaults
 * (baked into the var() fallbacks) render byte-identically to before.
 *
 * `deriveAccentVars` is the single source of truth for the palette math. It is
 * a pure function so it can be unit-tested without a DOM.
 */

/** The CSS variables DemoView reads for its accent palette. */
export interface AccentVars {
  '--floe-accent': string;
  /** 90% (E6) variant used as the second stop of accent gradients. */
  '--floe-accent-strong': string;
  /** Lightened hover tint (~18% toward white) for gradient hovers. */
  '--floe-accent-hover': string;
  /** 35% glow — box-shadows behind CTAs and the speaking-state dot. */
  '--floe-accent-glow': string;
  /** 12% wash — muted accent surfaces (chips, recap bullets). */
  '--floe-accent-muted': string;
  /** 20% — faint accent borders. */
  '--floe-accent-20': string;
  /** 15% — fainter accent borders. */
  '--floe-accent-15': string;
  /** 40% — markdown blockquote rule. */
  '--floe-accent-40': string;
  /** 8% — faint accent wash (e.g. the collapsed-mode brand chip). */
  '--floe-accent-08': string;
}

/** 6-digit hex only — must match floe-api's HEX_COLOR_PATTERN. The 8-digit
 *  strong variant (`#RRGGBBE6`) relies on a 6-digit base. */
const HEX6 = /^#[0-9a-fA-F]{6}$/;

const clampByte = (n: number): number => Math.max(0, Math.min(255, Math.round(n)));
const toHex2 = (n: number): string => clampByte(n).toString(16).padStart(2, '0');

/**
 * Turn a brand accent hex into the full CSS-variable palette DemoView needs.
 *
 * Returns `undefined` when the input is empty/missing/not a #RRGGBB hex — the
 * caller then sets NO variables and the overlay falls back to the Floe coral
 * defaults. This is the "sensible defaults when unset; no layout breakage"
 * guarantee (FLO-64 acceptance criterion).
 */
export function deriveAccentVars(accent?: string | null): AccentVars | undefined {
  if (!accent) return undefined;
  const hex = accent.trim();
  if (!HEX6.test(hex)) return undefined;

  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);

  // Hover = mix toward white by 18.4%. Tuned so the default coral (#FF5C35)
  // resolves exactly to #FF7A5A, matching the previous hand-picked hover
  // constant — so explicitly setting the accent to coral is a no-op.
  const lighten = (c: number): number => c + (255 - c) * 0.184;
  const hover = `#${toHex2(lighten(r))}${toHex2(lighten(g))}${toHex2(lighten(b))}`;
  const rgba = (a: number): string => `rgba(${r}, ${g}, ${b}, ${a})`;

  return {
    '--floe-accent': hex,
    '--floe-accent-strong': `${hex}E6`,
    '--floe-accent-hover': hover,
    '--floe-accent-glow': rgba(0.35),
    '--floe-accent-muted': rgba(0.12),
    '--floe-accent-20': rgba(0.2),
    '--floe-accent-15': rgba(0.15),
    '--floe-accent-40': rgba(0.4),
    '--floe-accent-08': rgba(0.08),
  };
}
